import React, { Fragment, useEffect, useState } from 'react'
import { Dialog, Transition } from '@headlessui/react'
import type { TariffRateItem, TariffStatus } from '../../../apis/types'
import { updateTariffRateApi } from '../../../apis/apis'
import { FieldTooltip, PCIA_YEAR_HINT } from './TariffFieldKey'
import { isActiveRatesetStatus, lockedRateWindow } from './tariffHistoryUtils'

const RATE_GROUPS = [
    'GENERATION',
    'DELIVERY',
    'DEMAND',
    'NBC',
    'BUNDLED_FEE',
    'PDP',
    'SERVICE',
    'NONE',
] as const

const SEASONS = ['SUMMER', 'WINTER', 'ALL'] as const
const TOU_PERIODS = ['PEAK', 'MID_PEAK', 'PARTIAL_PEAK', 'OFF_PEAK', 'SUPER_OFF_PEAK', 'ALL'] as const
const RATE_PERIOD_TYPES = ['DAILY', 'MONTHLY', 'ANNUAL'] as const

const inputClass =
    'block w-full rounded-md border border-slate-300 bg-white px-2 py-1.5 text-sm text-slate-900 shadow-sm focus:border-indigo-600 focus:ring-indigo-600 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100'

const DECIMAL_PATTERN = /^-?(?:\d+\.?\d*|\.\d+)$/

export const EditTariffRateModal: React.FC<{
    rate: TariffRateItem | null
    open: boolean
    onClose: () => void
    onSaved: (rate: TariffRateItem) => void
    historyEffectiveDate?: string | null
    historyEndDate?: string | null
    historyStatus?: TariffStatus
    siblingEndDates?: Array<string | null | undefined>
}> = ({
    rate,
    open,
    onClose,
    onSaved,
    historyEffectiveDate,
    historyEndDate,
    historyStatus,
    siblingEndDates,
}) => {
    const [name, setName] = useState('')
    const [amount, setAmount] = useState('')
    const [rateGroup, setRateGroup] = useState<string>('GENERATION')
    const [season, setSeason] = useState<string>('')
    const [touPeriod, setTouPeriod] = useState<string>('')
    const [ratePeriodType, setRatePeriodType] = useState<string>('')
    const [vintageYear, setVintageYear] = useState('')
    const [coincidental, setCoincidental] = useState(false)
    const [modifiedDescription, setModifiedDescription] = useState('')
    const [reason, setReason] = useState('')
    const [saving, setSaving] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const windowDates = lockedRateWindow({
        historyEffectiveDate,
        historyEndDate,
        historyStatus,
        siblingEndDates,
    })

    useEffect(() => {
        if (!open || rate == null) return
        setName(rate.name)
        setAmount(rate.rate.toFixed(6))
        setRateGroup(rate.rateGroup)
        setSeason(rate.season ?? '')
        setTouPeriod(rate.touPeriod ?? '')
        setRatePeriodType(rate.ratePeriodType ?? '')
        setVintageYear(rate.vintageYear ?? '')
        setCoincidental(rate.coincidental)
        setModifiedDescription(rate.modifiedDescription ?? '')
        setReason('')
        setError(null)
        setSaving(false)
    }, [open, rate])

    const handleSave = (): void => {
        if (rate == null) return
        const trimmedName = name.trim()
        const trimmedRate = amount.trim()
        if (trimmedName === '') {
            setError('Name is required.')
            return
        }
        if (trimmedRate === '' || !DECIMAL_PATTERN.test(trimmedRate)) {
            setError('Enter a valid decimal rate.')
            return
        }
        if (windowDates.effectiveDate === '') {
            setError('Effective date is required.')
            return
        }
        if (reason.trim() === '') {
            setError('Reason is required.')
            return
        }

        setSaving(true)
        setError(null)
        void updateTariffRateApi(rate.cuid, {
            name: trimmedName,
            rate: Number(trimmedRate),
            rateGroup,
            season: season === '' ? null : season,
            touPeriod: touPeriod === '' ? null : touPeriod,
            ratePeriodType: ratePeriodType === '' ? null : ratePeriodType,
            rateEffectiveDate: `${windowDates.effectiveDate}T00:00:00.000Z`,
            ...(windowDates.endDate != null
                ? { rateEndDate: `${windowDates.endDate}T00:00:00.000Z` }
                : {}),
            vintageYear: vintageYear.trim() === '' ? null : vintageYear.trim(),
            coincidental,
            modifiedDescription: modifiedDescription.trim() === '' ? undefined : modifiedDescription.trim(),
            reason: reason.trim(),
        })
            .then((updated) => {
                onSaved(updated)
                onClose()
            })
            .catch(() => setError('Failed to update rate.'))
            .finally(() => setSaving(false))
    }

    return (
        <Transition show={open} as={Fragment}>
            <Dialog as="div" className="relative z-50" onClose={() => !saving && onClose()}>
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-200"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-150"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-black/50 dark:bg-black/70" />
                </Transition.Child>
                <div className="fixed inset-0 overflow-y-auto">
                    <div className="flex min-h-full items-end justify-center p-4 sm:items-center">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-200"
                            enterFrom="opacity-0 translate-y-4 sm:scale-95"
                            enterTo="opacity-100 translate-y-0 sm:scale-100"
                            leave="ease-in duration-150"
                            leaveFrom="opacity-100 sm:scale-100"
                            leaveTo="opacity-0 translate-y-4 sm:scale-95"
                        >
                            <Dialog.Panel className="w-full max-w-2xl rounded-lg bg-white p-5 text-left shadow-xl dark:bg-slate-900">
                                <Dialog.Title className="text-lg font-medium text-slate-900 dark:text-slate-100">
                                    Edit rate
                                </Dialog.Title>
                                {rate != null ? (
                                    <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                                        {rate.type} · {rate.category}
                                    </p>
                                ) : null}
                                <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
                                    <label className="block text-xs font-medium text-slate-600 dark:text-slate-300">
                                        Name
                                        <input
                                            className={`${inputClass} mt-1`}
                                            value={name}
                                            onChange={(e) => setName(e.target.value)}
                                        />
                                    </label>
                                    <label className="block text-xs font-medium text-slate-600 dark:text-slate-300">
                                        Rate
                                        <input
                                            className={`${inputClass} mt-1`}
                                            inputMode="decimal"
                                            value={amount}
                                            onChange={(e) => setAmount(e.target.value)}
                                        />
                                    </label>
                                    <label className="block text-xs font-medium text-slate-600 dark:text-slate-300">
                                        Rate group
                                        <select
                                            className={`${inputClass} mt-1`}
                                            value={rateGroup}
                                            onChange={(e) => setRateGroup(e.target.value)}
                                        >
                                            {RATE_GROUPS.map((v) => (
                                                <option key={v} value={v}>
                                                    {v}
                                                </option>
                                            ))}
                                        </select>
                                    </label>
                                    <label className="block text-xs font-medium text-slate-600 dark:text-slate-300">
                                        Season
                                        <select
                                            className={`${inputClass} mt-1`}
                                            value={season}
                                            onChange={(e) => setSeason(e.target.value)}
                                        >
                                            <option value="">—</option>
                                            {SEASONS.map((v) => (
                                                <option key={v} value={v}>
                                                    {v}
                                                </option>
                                            ))}
                                        </select>
                                    </label>
                                    <label className="block text-xs font-medium text-slate-600 dark:text-slate-300">
                                        TOU period
                                        <select
                                            className={`${inputClass} mt-1`}
                                            value={touPeriod}
                                            onChange={(e) => setTouPeriod(e.target.value)}
                                        >
                                            <option value="">—</option>
                                            {TOU_PERIODS.map((v) => (
                                                <option key={v} value={v}>
                                                    {v}
                                                </option>
                                            ))}
                                        </select>
                                    </label>
                                    <label className="block text-xs font-medium text-slate-600 dark:text-slate-300">
                                        Rate period type
                                        <select
                                            className={`${inputClass} mt-1`}
                                            value={ratePeriodType}
                                            onChange={(e) => setRatePeriodType(e.target.value)}
                                        >
                                            <option value="">—</option>
                                            {RATE_PERIOD_TYPES.map((v) => (
                                                <option key={v} value={v}>
                                                    {v}
                                                </option>
                                            ))}
                                        </select>
                                    </label>
                                    <div className="block text-xs font-medium text-slate-600 dark:text-slate-300">
                                        Effective date
                                        <p className="mt-1 text-sm text-slate-900 dark:text-slate-100">
                                            {windowDates.effectiveDate !== ''
                                                ? windowDates.effectiveDate
                                                : '—'}
                                        </p>
                                    </div>
                                    <div className="block text-xs font-medium text-slate-600 dark:text-slate-300">
                                        End date
                                        <p className="mt-1 text-sm text-slate-900 dark:text-slate-100">
                                            {windowDates.endLabel}
                                        </p>
                                    </div>
                                    {historyStatus != null && isActiveRatesetStatus(historyStatus) ? (
                                        <p className="sm:col-span-2 text-xs text-amber-800 dark:text-amber-200">
                                            Bills that already used this rateset will change.
                                        </p>
                                    ) : null}
                                    <label className="block text-xs font-medium text-slate-600 dark:text-slate-300">
                                        <FieldTooltip content={PCIA_YEAR_HINT}>PCIA year</FieldTooltip>
                                        <input
                                            className={`${inputClass} mt-1`}
                                            value={vintageYear}
                                            onChange={(e) => setVintageYear(e.target.value)}
                                        />
                                    </label>
                                    <label className="flex items-center gap-2 self-end text-xs font-medium text-slate-600 dark:text-slate-300">
                                        <input
                                            type="checkbox"
                                            className="rounded border-slate-300 text-indigo-600 focus:ring-indigo-600"
                                            checked={coincidental}
                                            onChange={(e) => setCoincidental(e.target.checked)}
                                        />
                                        Coincidental
                                    </label>
                                    <label className="block text-xs font-medium text-slate-600 dark:text-slate-300 sm:col-span-2">
                                        Notes
                                        <textarea
                                            className={`${inputClass} mt-1`}
                                            rows={2}
                                            value={modifiedDescription}
                                            onChange={(e) => setModifiedDescription(e.target.value)}
                                        />
                                    </label>
                                    <label className="block text-xs font-medium text-slate-600 dark:text-slate-300 sm:col-span-2">
                                        Reason{' '}
                                        <span className="font-normal text-slate-500">(required)</span>
                                        <textarea
                                            className={`${inputClass} mt-1`}
                                            rows={2}
                                            maxLength={500}
                                            value={reason}
                                            onChange={(e) => setReason(e.target.value)}
                                        />
                                    </label>
                                </div>
                                {error != null ? (
                                    <p className="mt-3 text-sm text-red-600 dark:text-red-400">{error}</p>
                                ) : null}
                                <div className="mt-5 flex justify-end gap-2">
                                    <button
                                        type="button"
                                        className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200"
                                        onClick={onClose}
                                        disabled={saving}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="button"
                                        className="rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-indigo-500 disabled:opacity-60"
                                        onClick={handleSave}
                                        disabled={saving || reason.trim() === ''}
                                    >
                                        {saving ? 'Saving…' : 'Save changes'}
                                    </button>
                                </div>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition>
    )
}
